import styled, { css } from "styled-components";
import tw from "twin.macro";

import { NavAlignType, NavRoleType } from "./Nav";

interface StyledNavProps {
  $align?: NavAlignType;
  $role?: NavRoleType;
}

export const StyledNav = styled.nav<StyledNavProps>`
  ${tw`tw-flex`}
  height: 100%;
  width: 100%;
  overflow: clip visible;

  ${(props) =>
    props.$align === "left" &&
    css`
      ${tw`tw-justify-start`}
    `}

  ${(props) =>
    props.$align === "center" &&
    css`
      ${tw`tw-justify-center`}
    `}

  ${(props) =>
    props.$align === "right" &&
    css`
      ${tw`tw-justify-end`}
    `}

  ${(props) =>
    props.$role === "light" &&
    css`
      height: auto;
    `}
`;

StyledNav.displayName = "StyledNav";

export const StyledNavList = styled.ul`
  ${tw`tw-flex tw-list-none tw-m-0 tw-p-0`}
  overflow: clip visible;
`;

StyledNavList.displayName = "StyledNavList";

interface StyledNavListItemProps {
  $active?: boolean;
  $disabled?: boolean;
  $role?: NavRoleType;
  $asMore?: boolean;
}

export const StyledNavListItem = styled.li<StyledNavListItemProps>`
  ${tw`tw-text-sm tw-cursor-default tw-relative tw-select-none`}
  color: var(--color-theme-700);
  font-size: 15px;
  display: flex;
  align-items: center;
  font-weight: 500;
  padding: 0 10px;
  flex-shrink: 0;

  &:hover,
  &:hover * {
    ${(props) =>
      !props.$disabled &&
      css`
        ${tw`tw-text-primary`}
      `}
  }

  ${(props) =>
    !props.$active &&
    !props.$disabled &&
    css`
      ${tw`tw-cursor-pointer`}
    `}

  &::after {
    content: "";
    ${tw`tw-absolute tw-block tw-m-auto tw-w-0 tw-bg-transparent`}
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 3px;
    transition: width 0.3s ease, background-color 0.3s ease;
  }

  ${(props) =>
    props.$active &&
    css`
      ${tw`tw-pointer-events-none`}
      &::after {
        ${tw`tw-w-full tw-bg-primary`}
      }
    `}

  ${(props) =>
    props.$disabled &&
    css`
      ${tw`tw-select-none tw-cursor-not-allowed tw-opacity-50`}
    `}

  ${(props) =>
    props.$role === "light" &&
    css`
      ${tw`tw-text-sm`}
      color: var(--color-theme-800);
      font-weight: 400;
      padding: 0 8px;

      &::after {
        bottom: -4px;
        height: 2px;
      }
    `}

  ${(props) =>
    props.$active &&
    css`
      ${tw`tw-text-primary`}
      & * {
        ${tw`tw-text-primary`}
      }
    `}
`;

StyledNavListItem.displayName = "StyledNavListItem";

export const StyledNavAsSelect = styled.div<{ $active: boolean }>`
  padding: 0 10px;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: var(--color-primary);
    top: 1px;
    position: relative;
  }

  ${(props) =>
    props.$active &&
    css`
      ${tw`tw-text-primary`}
      & * {
        ${tw`tw-text-primary`}
      }
    `}
`;

StyledNavAsSelect.displayName = "StyledNavAsSelect";

export const StyledNavAsMoreTarget = styled.div<{
  $role?: NavRoleType;
}>`
  padding: 0 10px;
  height: 100%;
  display: flex;
  align-items: center;

  ${(props) =>
    props.$role === "light" &&
    css`
      padding: 0;
    `}
`;

StyledNavAsMoreTarget.displayName = "StyledNavAsMoreTarget";
